import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle2, Upload, Image } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SignatureUpload() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const tokenCode = searchParams.get('t') || '';
  const prefillName = searchParams.get('n') || '';
  const prefillPhone = searchParams.get('p') || '';
  const prefillEmail = searchParams.get('e') || '';

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [name, setName] = useState(prefillName);
  const [phone, setPhone] = useState(prefillPhone);
  const [email, setEmail] = useState(prefillEmail);
  const [hasPrefill, setHasPrefill] = useState(!!(prefillName || prefillPhone || prefillEmail));
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/events/${slug}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => { setEvent(data); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
    if (tokenCode && !hasPrefill) {
      fetch(`${API_URL}/api/tokens/${tokenCode}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) { setName(data.name); setPhone(data.phone); setEmail(data.email); setHasPrefill(true); } });
    }
  }, [slug, tokenCode]);

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (f) {
      setFile(f);
      setPreview(URL.createObjectURL(f));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !name.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_URL}/api/events/${slug}/signature?name=${encodeURIComponent(name)}&phone=${encodeURIComponent(phone)}&email=${encodeURIComponent(email)}`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(true);
      } else {
        setError(data.detail || 'Upload failed. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  if (loading) return <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center"><p className="text-white">Loading...</p></div>;

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Not Found</h1>
          <p className="text-gray-400">This upload link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-[#F5A623]/10 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-[#F5A623]" />
            </div>
          </div>
          <h1 data-testid="upload-success-title" className="text-3xl font-black text-white mb-4">Signature Uploaded!</h1>
          <p className="text-gray-400">Thank you, your signature has been received. Our team will review it.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="bg-[#1A1A1D] border border-white/10 rounded-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-[#F5A623] to-[#E59400] p-6 text-center">
            <h1 className="text-2xl sm:text-3xl font-black text-black mb-1">{event?.name}</h1>
            <p className="text-black/70 text-sm">Upload Your Signature</p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-5">
            {hasPrefill ? (
              <p className="text-gray-400 text-sm">Hi <span className="text-white font-medium">{name}</span>, please upload your signature below.</p>
            ) : (
              <>
                <div className="space-y-1.5">
                  <Label className="text-gray-300 text-sm">Full Name *</Label>
                  <Input data-testid="sig-name" required value={name} onChange={(e) => setName(e.target.value)} className="bg-[#0A0A0B] border-white/20 text-white h-11" placeholder="Enter your full name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-gray-300 text-sm">Mobile</Label>
                    <Input data-testid="sig-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="bg-[#0A0A0B] border-white/20 text-white h-11" placeholder="+60 12 345 6789" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-gray-300 text-sm">Email</Label>
                    <Input data-testid="sig-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-[#0A0A0B] border-white/20 text-white h-11" placeholder="your@email.com" />
                  </div>
                </div>
              </>
            )}

            {/* Upload Area */}
            <div className="space-y-1.5">
              <Label className="text-gray-300 text-sm">Signature Image *</Label>
              <label
                data-testid="sig-upload-area"
                className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-[#F5A623]/50 transition-colors bg-[#0A0A0B] overflow-hidden"
              >
                {preview ? (
                  <img src={preview} alt="Preview" className="max-h-full max-w-full object-contain p-2" />
                ) : (
                  <div className="flex flex-col items-center gap-2 text-gray-500">
                    <Upload className="w-8 h-8" />
                    <span className="text-sm">Click to upload or drag & drop</span>
                    <span className="text-xs">PNG, JPG, or HEIC</span>
                  </div>
                )}
                <input type="file" accept="image/*" onChange={handleFile} className="hidden" />
              </label>
              {file && <p className="text-gray-400 text-xs mt-1">{file.name}</p>}
            </div>

            {error && <p data-testid="sig-error" className="text-red-400 text-sm">{error}</p>}

            <Button data-testid="sig-submit-btn" type="submit" disabled={submitting || !file || !name.trim()} className="w-full bg-[#F5A623] hover:bg-[#E59400] text-black font-bold h-12 text-lg">
              {submitting ? 'Uploading...' : 'Submit Signature'}
            </Button>
          </form>
        </div>
        <p className="text-center text-gray-600 text-xs mt-6">Signature of Wealth by Steven Yong</p>
      </div>
    </div>
  );
}
